<script lang="ts">
    import { onMount, onDestroy } from "svelte";
    import { ConfigurationManager } from "../../ConfigurationManager";
    import {
    DefaultStatsController,
        type ImpressionsByCountry,
        type IStatsController,
    } from "../../controller";
    import { DefaultErrorHandler } from "../../utility/ContextHelper.svelte";

    import * as ChartGeo from "chartjs-chart-geo";

    import { Chart, registerables } from "chart.js";
    import { ChoroplethController} from "chartjs-chart-geo";
    import {type Feature, type Geometry} from "geojson"
    import {type GeometryCollection, type Properties, type Topology} from "topojson-specification/index"
    import type { BaseProps } from "../BaseProps";

    let {
        id,
        isBooking = true,
        from,
        to,
        classes,
        style
    }: BaseProps & {
        id: string,
        isBooking?: boolean,//else it is a slot id
        from: Date,
        to: Date
    } = $props();

    let statsController: IStatsController =
        ConfigurationManager.getInstance().statsController;

    let funcSort = (
        s: ImpressionsByCountry,
    ): { code: string; count: number }[] => {
        let result: { code: string; count: number }[] = [];
        for (let c of Object.keys(s)) {
            result.push({
                code: c,
                count: s[c],
            });
        }

        result.sort(
            (
                a: { code: string; count: number },
                b: { code: string; count: number },
            ): number => {
                if (a.count < b.count) {
                    return 1;
                } else if (a.count > b.count) {
                    return -1;
                } else {
                    return 0;
                }
            },
        );
        return result;
    };
    let stats: ImpressionsByCountry = $state({});

    let statsSorted: { code: string; count: number }[] = $state([]);
    $effect(() => {
        statsSorted = funcSort(stats);
    });


    let chart: Chart;
    let canvasElement: HTMLCanvasElement | null = null;
    let isDragging = $state(false);
    let currentZoom = 1;
    let panOffset: [number, number] = [0, 0];
    let dragStart = { x: 0, y: 0, panX: 0, panY: 0 };

    function applyZoomPan() {
        if (!chart) return;
        (chart.options.scales as any).projection.projectionScale = currentZoom;
        (chart.options.scales as any).projection.projectionOffset = [panOffset[0], panOffset[1]] as [number, number];
        chart.update('none');
    }

    function zoomMap(factor: number, mouseOffsetX: number = 0, mouseOffsetY: number = 0) {
        const oldZoom = currentZoom;
        currentZoom = Math.max(0.3, Math.min(currentZoom * factor, 30));
        const ratio = currentZoom / oldZoom;
        panOffset = [
            mouseOffsetX + (panOffset[0] - mouseOffsetX) * ratio,
            mouseOffsetY + (panOffset[1] - mouseOffsetY) * ratio,
        ];
        applyZoomPan();
    }

    function onCanvasWheel(event: WheelEvent) {
        event.preventDefault();
        const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
        const rect = canvasElement!.getBoundingClientRect();
        const mouseOffsetX = event.clientX - rect.left - rect.width / 2;
        const mouseOffsetY = event.clientY - rect.top - rect.height / 2;
        zoomMap(factor, mouseOffsetX, mouseOffsetY);
    }

    function onDocumentMouseMove(event: MouseEvent) {
        const dx = event.clientX - dragStart.x;
        const dy = event.clientY - dragStart.y;
        panOffset = [dragStart.panX + dx, dragStart.panY + dy];
        applyZoomPan();
    }

    function onDocumentMouseUp() {
        isDragging = false;
        document.removeEventListener("mousemove", onDocumentMouseMove);
        document.removeEventListener("mouseup", onDocumentMouseUp);
    }

    function onCanvasMouseDown(event: MouseEvent) {
        if (event.button !== 0) return;
        isDragging = true;
        dragStart = { x: event.clientX, y: event.clientY, panX: panOffset[0], panY: panOffset[1] };
        document.addEventListener("mousemove", onDocumentMouseMove);
        document.addEventListener("mouseup", onDocumentMouseUp);
    }

    onDestroy(() => {
        if (canvasElement) {
            canvasElement.removeEventListener("wheel", onCanvasWheel);
            canvasElement.removeEventListener("mousedown", onCanvasMouseDown);
        }
        document.removeEventListener("mousemove", onDocumentMouseMove);
        document.removeEventListener("mouseup", onDocumentMouseUp);
    });

    let countries: Feature<Geometry, {[name: string]: any;}>[];
    let atlasNameByNormalized: Map<string, string>;

    const ISO_ALPHA2_TO_ENGLISH_NAME: { [code: string]: string } = {
        AD: "Andorra",
        AE: "United Arab Emirates",
        AF: "Afghanistan",
        AG: "Antigua and Barbuda",
        AI: "Anguilla",
        AL: "Albania",
        AM: "Armenia",
        AO: "Angola",
        AQ: "Antarctica",
        AR: "Argentina",
        AS: "American Samoa",
        AT: "Austria",
        AU: "Australia",
        AW: "Aruba",
        AX: "Aland Islands",
        AZ: "Azerbaijan",
        BA: "Bosnia and Herzegovina",
        BB: "Barbados",
        BD: "Bangladesh",
        BE: "Belgium",
        BF: "Burkina Faso",
        BG: "Bulgaria",
        BH: "Bahrain",
        BI: "Burundi",
        BJ: "Benin",
        BL: "Saint Barthelemy",
        BM: "Bermuda",
        BN: "Brunei",
        BO: "Bolivia",
        BQ: "Bonaire, Sint Eustatius and Saba",
        BR: "Brazil",
        BS: "Bahamas",
        BT: "Bhutan",
        BV: "Bouvet Island",
        BW: "Botswana",
        BY: "Belarus",
        BZ: "Belize",
        CA: "Canada",
        CC: "Cocos (Keeling) Islands",
        CD: "Democratic Republic of the Congo",
        CF: "Central African Republic",
        CG: "Republic of the Congo",
        CH: "Switzerland",
        CI: "Cote d'Ivoire",
        CK: "Cook Islands",
        CL: "Chile",
        CM: "Cameroon",
        CN: "China",
        CO: "Colombia",
        CR: "Costa Rica",
        CU: "Cuba",
        CV: "Cabo Verde",
        CW: "Curacao",
        CX: "Christmas Island",
        CY: "Cyprus",
        CZ: "Czechia",
        DE: "Germany",
        DJ: "Djibouti",
        DK: "Denmark",
        DM: "Dominica",
        DO: "Dominican Republic",
        DZ: "Algeria",
        EC: "Ecuador",
        EE: "Estonia",
        EG: "Egypt",
        EH: "Western Sahara",
        ER: "Eritrea",
        ES: "Spain",
        ET: "Ethiopia",
        FI: "Finland",
        FJ: "Fiji",
        FK: "Falkland Islands",
        FM: "Micronesia",
        FO: "Faroe Islands",
        FR: "France",
        GA: "Gabon",
        GB: "United Kingdom",
        GD: "Grenada",
        GE: "Georgia",
        GF: "French Guiana",
        GG: "Guernsey",
        GH: "Ghana",
        GI: "Gibraltar",
        GL: "Greenland",
        GM: "Gambia",
        GN: "Guinea",
        GP: "Guadeloupe",
        GQ: "Equatorial Guinea",
        GR: "Greece",
        GS: "South Georgia and the South Sandwich Islands",
        GT: "Guatemala",
        GU: "Guam",
        GW: "Guinea-Bissau",
        GY: "Guyana",
        HK: "Hong Kong",
        HM: "Heard Island and McDonald Islands",
        HN: "Honduras",
        HR: "Croatia",
        HT: "Haiti",
        HU: "Hungary",
        ID: "Indonesia",
        IE: "Ireland",
        IL: "Israel",
        IM: "Isle of Man",
        IN: "India",
        IO: "British Indian Ocean Territory",
        IQ: "Iraq",
        IR: "Iran",
        IS: "Iceland",
        IT: "Italy",
        JE: "Jersey",
        JM: "Jamaica",
        JO: "Jordan",
        JP: "Japan",
        KE: "Kenya",
        KG: "Kyrgyzstan",
        KH: "Cambodia",
        KI: "Kiribati",
        KM: "Comoros",
        KN: "Saint Kitts and Nevis",
        KP: "North Korea",
        KR: "South Korea",
        KW: "Kuwait",
        KY: "Cayman Islands",
        KZ: "Kazakhstan",
        LA: "Laos",
        LB: "Lebanon",
        LC: "Saint Lucia",
        LI: "Liechtenstein",
        LK: "Sri Lanka",
        LR: "Liberia",
        LS: "Lesotho",
        LT: "Lithuania",
        LU: "Luxembourg",
        LV: "Latvia",
        LY: "Libya",
        MA: "Morocco",
        MC: "Monaco",
        MD: "Moldova",
        ME: "Montenegro",
        MF: "Saint Martin",
        MG: "Madagascar",
        MH: "Marshall Islands",
        MK: "North Macedonia",
        ML: "Mali",
        MM: "Myanmar",
        MN: "Mongolia",
        MO: "Macao",
        MP: "Northern Mariana Islands",
        MQ: "Martinique",
        MR: "Mauritania",
        MS: "Montserrat",
        MT: "Malta",
        MU: "Mauritius",
        MV: "Maldives",
        MW: "Malawi",
        MX: "Mexico",
        MY: "Malaysia",
        MZ: "Mozambique",
        NA: "Namibia",
        NC: "New Caledonia",
        NE: "Niger",
        NF: "Norfolk Island",
        NG: "Nigeria",
        NI: "Nicaragua",
        NL: "Netherlands",
        NO: "Norway",
        NP: "Nepal",
        NR: "Nauru",
        NU: "Niue",
        NZ: "New Zealand",
        OM: "Oman",
        PA: "Panama",
        PE: "Peru",
        PF: "French Polynesia",
        PG: "Papua New Guinea",
        PH: "Philippines",
        PK: "Pakistan",
        PL: "Poland",
        PM: "Saint Pierre and Miquelon",
        PN: "Pitcairn",
        PR: "Puerto Rico",
        PS: "Palestine",
        PT: "Portugal",
        PW: "Palau",
        PY: "Paraguay",
        QA: "Qatar",
        RE: "Reunion",
        RO: "Romania",
        RS: "Serbia",
        RU: "Russia",
        RW: "Rwanda",
        SA: "Saudi Arabia",
        SB: "Solomon Islands",
        SC: "Seychelles",
        SD: "Sudan",
        SE: "Sweden",
        SG: "Singapore",
        SH: "Saint Helena, Ascension and Tristan da Cunha",
        SI: "Slovenia",
        SJ: "Svalbard and Jan Mayen",
        SK: "Slovakia",
        SL: "Sierra Leone",
        SM: "San Marino",
        SN: "Senegal",
        SO: "Somalia",
        SR: "Suriname",
        SS: "South Sudan",
        ST: "Sao Tome and Principe",
        SV: "El Salvador",
        SX: "Sint Maarten",
        SY: "Syria",
        SZ: "Eswatini",
        TC: "Turks and Caicos Islands",
        TD: "Chad",
        TF: "French Southern Territories",
        TG: "Togo",
        TH: "Thailand",
        TJ: "Tajikistan",
        TK: "Tokelau",
        TL: "Timor-Leste",
        TM: "Turkmenistan",
        TN: "Tunisia",
        TO: "Tonga",
        TR: "Turkey",
        TT: "Trinidad and Tobago",
        TV: "Tuvalu",
        TW: "Taiwan",
        TZ: "Tanzania",
        UA: "Ukraine",
        UG: "Uganda",
        UM: "United States Minor Outlying Islands",
        US: "United States",
        UY: "Uruguay",
        UZ: "Uzbekistan",
        VA: "Vatican City",
        VC: "Saint Vincent and the Grenadines",
        VE: "Venezuela",
        VG: "British Virgin Islands",
        VI: "United States Virgin Islands",
        VN: "Vietnam",
        VU: "Vanuatu",
        WF: "Wallis and Futuna",
        WS: "Samoa",
        YE: "Yemen",
        YT: "Mayotte",
        ZA: "South Africa",
        ZM: "Zambia",
        ZW: "Zimbabwe",
    };

    const COUNTRY_NAME_ALIASES_TO_WORLD_ATLAS: {
        [normalizedName: string]: string;
    } = {
        "antigua and barbuda": "Antigua and Barb.",
        "aland islands": "Åland",
        "bosnia and herzegovina": "Bosnia and Herz.",
        "saint barthelemy": "St-Barthélemy",
        "democratic republic of the congo": "Dem. Rep. Congo",
        "central african republic": "Central African Rep.",
        "republic of the congo": "Congo",
        "cook islands": "Cook Is.",
        "dominican republic": "Dominican Rep.",
        "western sahara": "W. Sahara",
        "falkland islands": "Falkland Is.",
        "faroe islands": "Faeroe Is.",
        "equatorial guinea": "Eq. Guinea",
        "south georgia and the south sandwich islands": "S. Geo. and the Is.",
        "heard island and mcdonald islands": "Heard I. and McDonald Is.",
        "british indian ocean territory": "Br. Indian Ocean Ter.",
        "saint kitts and nevis": "St. Kitts and Nevis",
        "cayman islands": "Cayman Is.",
        "saint martin": "St-Martin",
        "marshall islands": "Marshall Is.",
        "north macedonia": "Macedonia",
        "northern mariana islands": "N. Mariana Is.",
        "french polynesia": "Fr. Polynesia",
        "saint pierre and miquelon": "St. Pierre and Miquelon",
        pitcairn: "Pitcairn Is.",
        "solomon islands": "Solomon Is.",
        "saint helena ascension and tristan da cunha": "Saint Helena",
        "south sudan": "S. Sudan",
        "turks and caicos islands": "Turks and Caicos Is.",
        "united states": "United States of America",
        "vatican city": "Vatican",
        "saint vincent and the grenadines": "St. Vin. and Gren.",
        "british virgin islands": "British Virgin Is.",
        "united states virgin islands": "U.S. Virgin Is.",
        "wallis and futuna": "Wallis and Futuna Is.",
    };

    function normalizeCountryName(name: string): string {
        return name
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-zA-Z0-9]+/g, " ")
            .trim()
            .toLowerCase();
    }

    function resolveToWorldAtlasCountryName(
        countryName: string,
        atlasNameByNormalized: Map<string, string>,
    ): string | undefined {
        const normalized = normalizeCountryName(countryName);
        const alias = COUNTRY_NAME_ALIASES_TO_WORLD_ATLAS[normalized];
        if (alias) {
            return alias;
        }

        return atlasNameByNormalized.get(normalized);
    }

    function mapIsoCodeToEnglishCountryName(
        isoCode: string,
    ): string | undefined {
        const normalized = isoCode.trim().toUpperCase();

        if (!normalized || normalized === "UNKNOWN") {
            return undefined;
        }

        if (normalized.length !== 2) {
            return undefined;
        }

        return ISO_ALPHA2_TO_ENGLISH_NAME[normalized];
    }

    onMount(() => {
        startChart();
    });

    function startChart() {
        Chart.register(
            ChoroplethController,
            ChartGeo.GeoFeature,
            ChartGeo.ProjectionScale,
            ChartGeo.ColorScale,
            ...registerables,
        );

        fetch("https://unpkg.com/world-atlas/countries-50m.json")
            .then((r) => r.json())
            .then((data) => {
                let test = ChartGeo.topojson.feature(
                    data as Topology,
                    data.objects.countries as GeometryCollection<Properties>,
                );
                countries = test.features;

                atlasNameByNormalized = new Map();
                for (let country of countries) {
                    atlasNameByNormalized.set(
                        normalizeCountryName(country.properties.name),
                        country.properties.name,
                    );
                }

                //@ts-ignore
                canvasElement = document.getElementById("geocanvas") as HTMLCanvasElement;
                canvasElement.addEventListener("wheel", onCanvasWheel, { passive: false });
                canvasElement.addEventListener("mousedown", onCanvasMouseDown);

                // Resolve --bs-primary to RGB for use in the choropleth color scale
                const primaryColorStr = getComputedStyle(document.documentElement).getPropertyValue('--bs-success').trim();
                let primaryR = 13, primaryG = 110, primaryB = 253; // Bootstrap default fallback
                const hexMatch = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(primaryColorStr);
                if (hexMatch) {
                    primaryR = parseInt(hexMatch[1], 16);
                    primaryG = parseInt(hexMatch[2], 16);
                    primaryB = parseInt(hexMatch[3], 16);
                } else {
                    const rgbMatch = /^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i.exec(primaryColorStr);
                    if (rgbMatch) {
                        primaryR = parseInt(rgbMatch[1]);
                        primaryG = parseInt(rgbMatch[2]);
                        primaryB = parseInt(rgbMatch[3]);
                    }
                }
                //@ts-ignore
                chart = new Chart(canvasElement.getContext("2d"),
                    {
                        type: "choropleth",
                        data: {
                            labels: countries.map((d) => d.properties.name),
                            datasets: [
                                {
                                    label: "Countries",
                                    data: countries.map((d) => ({
                                        feature: d,
                                        value: 0,
                                    })),
                                },
                            ],
                        },
                        options: {
                            showOutline: true,
                            showGraticule: true,
                            plugins: {
                                legend: {
                                    display: false,
                                },
                            },
                            scales: {
                                projection: {
                                    axis: "x",
                                    projection: "equalEarth",
                                },
                                color: {
                                    axis: 'x',
                                    interpolate: (v: number) => `rgb(${Math.round(255 + (primaryR - 255) * v)}, ${Math.round(255 + (primaryG - 255) * v)}, ${Math.round(255 + (primaryB - 255) * v)})`,
                                },
                            },
                        },
                    },
                );
                updateData(from, to);
            });
    }



    function updateData(f: Date, t: Date) {
        if (!countries || !chart) {
            return;
        }

        let promise: Promise<ImpressionsByCountry>;

        if(isBooking) {
            promise = statsController.getImpressionStatsForBookingPerIsoCountry(id, f, t);
        } else {
            promise = statsController.getImpressionStatsForSlotPerIsoCountry(id, f, t);
        }
        
            promise.then((result: ImpressionsByCountry) => {
                stats = result;

                let countryValueByName: Map<string, number> = new Map(
                    countries.map((d) => [d.properties.name, 0]),
                );
                for (let [isoCode, count] of Object.entries(result)) {
                    let countryName = mapIsoCodeToEnglishCountryName(isoCode);
                    if (countryName) {
                        let worldAtlasName = resolveToWorldAtlasCountryName(
                            countryName,
                            atlasNameByNormalized,
                        );
                        if (worldAtlasName) {
                            countryValueByName.set(worldAtlasName, count);
                        }
                    }
                }

                chart.data.datasets = [
                    {
                        label: "Countries",
                        data: countries.map((d) => ({
                            feature: d,
                            value: countryValueByName.get(d.properties.name) ?? 0,
                        })),
                    },
                ];
                chart.update();
            })
            .catch(DefaultErrorHandler);
    }

    $effect(() => {
        updateData(from, to);
    });

    function highlightCountry(isoCode: string | null) {
        if (!chart || !countries) return;
        if (isoCode === null) {
            chart.setActiveElements([]);
            chart.tooltip.setActiveElements([], { x: 0, y: 0 });
        } else {
            const countryName = mapIsoCodeToEnglishCountryName(isoCode);
            if (!countryName) {
                chart.setActiveElements([]);
                chart.tooltip.setActiveElements([], { x: 0, y: 0 });
            } else {
                const worldAtlasName = resolveToWorldAtlasCountryName(countryName, atlasNameByNormalized);
                if (!worldAtlasName) return;
                const index = countries.findIndex(d => d.properties.name === worldAtlasName);
                if (index === -1) return;
                chart.setActiveElements([{ datasetIndex: 0, index }]);
                chart.tooltip.setActiveElements([{ datasetIndex: 0, index }], { x: 0, y: 0 });
            }
        }
        chart.update();
    }
    
</script>

<div class={classes} style={style}>
    <div class="row">
        <div class="col-12">
            <strong>From</strong> {from.toLocaleDateString()} - {from.toLocaleTimeString()} <strong>to</strong> {to.toLocaleDateString()} - {to.toLocaleTimeString()}</div>
        </div>
    <div class="row align-items-start">
        <div class="col-4">
            <table class="w-100">
                <thead>
                    <tr>
                        <th>Country</th>
                        <th>Count</th>
                    </tr>
                </thead>
                <tbody>

                    {#each statsSorted as stat}
                        <tr
                            onmouseenter={() => highlightCountry(stat.code)}
                            onmouseleave={() => highlightCountry(null)}
                            style="cursor: pointer;"
                        >
                            <td>
                                {#if stat.code == "unknown"}
                                    <i>{stat.code}</i>
                                {:else}
                                    {stat.code}
                                {/if}
                            </td>
                            <td>{stat.count}</td>
                        </tr>
                    {:else}
                        <tr>
                            <td colspan="2">No items</td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <div class="col-8" style="position: relative;">
            <canvas id="geocanvas" style:cursor={isDragging ? 'grabbing' : 'grab'}> </canvas>
            <div style="position: absolute; bottom: 8px; right: 8px; display: flex; flex-direction: column; gap: 4px; z-index: 10;">
                <button
                    onclick={() => zoomMap(1.2)}
                    title="Zoom in"
                    style="width: 32px; height: 32px; border: 1px solid #999; background: white; border-radius: 4px; cursor: pointer; font-size: 18px; font-weight: bold; line-height: 1; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,0.25);"
                >+</button>
                <button
                    onclick={() => zoomMap(1 / 1.2)}
                    title="Zoom out"
                    style="width: 32px; height: 32px; border: 1px solid #999; background: white; border-radius: 4px; cursor: pointer; font-size: 22px; font-weight: bold; line-height: 1; display: flex; align-items: center; justify-content: center; box-shadow: 0 1px 4px rgba(0,0,0,0.25);"
                >−</button>
            </div>
        </div>
    </div>
</div>
